import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, filter, first, map, Observable, of, tap, timeout } from 'rxjs';
import { AppSelectors } from '../store/app/app.selectors';
import { SetAppStateFromAccountLockedGuard, SetSessionExpired } from '../store/app/app.actions';

@Injectable({
  providedIn: 'root',
})
export class AccountLockGuard {
  readonly store = inject(Store);
  readonly router = inject(Router);

  readonly fundAccountRoute: string = '/fund-account';

  canActivate(state: RouterStateSnapshot): Observable<boolean> {
    return state.url === this.fundAccountRoute
      ? of<boolean>(true)
      : this.store.select(AppSelectors.isAccountLocked).pipe(
          tap((accountLocked) => {
            if (accountLocked === undefined) {
              this.store.dispatch(new SetAppStateFromAccountLockedGuard());
            }
          }),
          filter((accountLocked) => accountLocked !== undefined),
          first(),
          timeout(8000), // 8 second timeout to prevent hanging on failed API calls
          catchError(() => {
            // If the API call times out or fails, trigger session expired flow
            this.store.dispatch(new SetSessionExpired(true));
            return of(false);
          }),
          map((accountLocked) => {
            if (accountLocked) {
              this.router.navigate([this.fundAccountRoute]);
            }
            return !accountLocked;
          }),
        );
  }
}

export const canActivateAccountLock: CanActivateFn = (_route, state) => {
  return inject(AccountLockGuard).canActivate(state);
};
